import { parseDateOnly } from "./parseDateOnly";

/**
 * pricingEngine.js
 * ---------------------------------------------------------------------------
 * Centralized pricing calculator for Exqura — MATRIX MODEL.
 *
 * This replaces the earlier "base price + weekend/holiday overrides" model.
 * Every course, vehicle, and accommodation now carries a full pricing
 * matrix: one price per (day-type tab) × (player-count or room-type tier) ×
 * (round-count or night-count tier) combination, entered directly by the
 * admin — there is no separate "standard price" to fall back to.
 *
 * WHY THIS FILE EXISTS
 * Every screen that shows or charges a price (admin preview, booking flow,
 * checkout, order summary) imports from here, so the number a golfer sees
 * while picking options always matches what they're actually charged.
 *
 * ===========================================================================
 * SCHEMA
 * ===========================================================================
 *
 * course.pricing_matrix / vehicle.pricing_matrix — 7 day-type keys:
 *   weekday, saturday_am, saturday_pm, sunday_am, sunday_pm, holiday_am, holiday_pm
 *
 *   course tier shape:   { p1to2: {r1, r2, r3plus}, p3plus: {r1, r2, r3plus} }
 *     - p1to2 / p3plus = total players in the booking (1-2 vs 3+)
 *     - r1 / r2 / r3plus = TOTAL rounds in the whole booking (not which
 *       round number this is) — the same per-player rate applies to every
 *       round in the trip once the total round count picks the column.
 *     - cell value is a PER-PLAYER rate.
 *
 *   vehicle tier shape:  { p1: {r1, r2, r3, addRound}, p2: {...}, p3plus: {...} }
 *     - p1 / p2 / p3plus = total players in the booking
 *     - r1 / r2 / r3 = TOTAL rounds in the whole booking; addRound = flat
 *       surcharge added for each round beyond 3
 *     - cell value is a FLAT total for the vehicle/trip — no per-seat
 *       vehicle-count math anymore (confirmed: "seats don't matter").
 *
 * course.holiday_dates / vehicle.holiday_dates: string[] 'yyyy-MM-dd' — just
 * marks which dates are holidays; the PRICE comes from the matrix's
 * holiday_am/holiday_pm cell (flat for every holiday date, not per-date).
 *
 * accommodation.pricing_matrix — 9 day-type keys:
 *   weekday, friday_am, friday_pm, saturday_am, saturday_pm, sunday_am,
 *   sunday_pm, holiday_am, holiday_pm
 *
 *   tier shape: { single_share: {n1, n2plus} }
 *     - single_share = one room (king bed), priced PER ROOM per night. The
 *       golfer picks how many rooms to book (defaults to one per player, but
 *       players may share 2-3 to a room). twin_share is legacy — the golfer
 *       no longer chooses it, so it is never read for new bookings.
 *     - n1 / n3plus = TOTAL nights in the whole stay (2 nights currently has
 *       no dedicated column in the admin UI — treated as n3plus; flag this
 *       if a true "2 nights" tier gets added later)
 *     - cell value is a per-night rate
 *
 *   Nights don't inherently have an AM/PM. ASSUMPTION (flagged, confirm with
 *   admin): a night's AM/PM follows whichever round, if any, is played on
 *   that calendar date — AM tee time that day → AM rate, PM tee time → PM
 *   rate, no round that day → AM. Callers pass this in via
 *   roundPeriodByDate (see calculateAccommodationTotal).
 *
 * accommodation.holiday_dates: string[] 'yyyy-MM-dd' — same flat-marker
 * pattern as course/vehicle.
 *
 * vehicle_free_for_one_round: boolean (course-level, unchanged from before)
 *   — still overrides the matrix entirely: free vehicle, hidden from the
 *   golfer, for a single-round booking on a course configured this way.
 *
 * ferry.price: ONE-WAY price per passenger — unchanged, still price × 2.
 * ---------------------------------------------------------------------------
 */

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** 'yyyy-MM-dd' from a Date, in local time. */
export function toDateKey(date) {
  if (!date) return null;
  const d = parseDateOnly(date);
  if (Number.isNaN(d.getTime())) return null;
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Maps a 'HH:mm' tee time to the AM/PM window used by the course's tee-slot
 * grid (AM 7:00-11:45, PM 12:00-14:30). Anything at or after 12:00 is PM.
 * Returns null if no time was given (callers should default to "AM"). */
export function deriveTeePeriod(time24) {
  if (!time24) return null;
  const [h] = time24.split(":").map(Number);
  if (Number.isNaN(h)) return null;
  return h >= 12 ? "PM" : "AM";
}

function isHolidayDate(date, holidayDates) {
  const key = toDateKey(date);
  return Boolean(
    key && Array.isArray(holidayDates) && holidayDates.includes(key),
  );
}

/** Day-type tab for course & vehicle matrices (7 keys). */
export function resolveDayType(date, period, holidayDates) {
  const p = period === "PM" ? "pm" : "am";
  if (isHolidayDate(date, holidayDates)) return `holiday_${p}`;
  if (!date) return "weekday";
  const day = parseDateOnly(date).getDay(); // 0=Sun..6=Sat
  if (day === 6) return `saturday_${p}`;
  if (day === 0) return `sunday_${p}`;
  return "weekday";
}

/** Day-type tab for the accommodation matrix (9 keys — adds Friday). */
export function resolveAccommodationDayType(date, period, holidayDates) {
  const p = period === "PM" ? "pm" : "am";
  if (isHolidayDate(date, holidayDates)) return `holiday_${p}`;
  if (!date) return "weekday";
  const day = parseDateOnly(date).getDay();
  if (day === 5) return `friday_${p}`;
  if (day === 6) return `saturday_${p}`;
  if (day === 0) return `sunday_${p}`;
  return "weekday";
}

/** Total-rounds-in-booking column, shared by course & vehicle matrices.
 * Course matrix tops out at "r3plus"; vehicle matrix tops out at "r3" plus
 * a per-extra-round "addRound" surcharge — pass maxTier accordingly. */
function roundCountTierKey(totalRounds, maxTier) {
  const n = Number(totalRounds) || 0;
  if (n <= 1) return "r1";
  if (n === 2) return "r2";
  return maxTier === "r3" ? "r3" : "r3plus";
}

/** Total-nights-in-stay column for accommodation. No dedicated "2 nights"
 * column in the admin UI — anything above 1 night uses n3plus. */
function nightCountTierKey(totalNights) {
  return Number(totalNights) > 1 ? "n2plus" : "n1";
}

// ---------------------------------------------------------------------------
// Vehicle-free-for-one-round override (requirement #1 — unchanged)
// ---------------------------------------------------------------------------

export function isVehicleFreeForBooking(course, numRounds) {
  return Boolean(course?.vehicle_free_for_one_round) && Number(numRounds) === 1;
}

export function shouldShowVehicleSelection(course, numRounds) {
  return !isVehicleFreeForBooking(course, numRounds);
}

// ---------------------------------------------------------------------------
// Course round pricing — matrix lookup, per-player rate
// ---------------------------------------------------------------------------

function playerTierGolf(players) {
  return Number(players) <= 2 ? "p1to2" : "p3plus";
}

/**
 * Per-player price for one round.
 * @param {object} params
 * @param {object} params.course
 * @param {Date|string} params.date
 * @param {'AM'|'PM'} [params.period]
 * @param {number} params.totalPlayers - total guests in the booking
 * @param {number} params.totalRounds - TOTAL rounds in the whole booking
 *   (same for every round — this is not "which round number is this")
 */
export function calculateCourseRoundPrice({
  course,
  date,
  period,
  totalPlayers,
  totalRounds,
}) {
  const dayType = resolveDayType(date, period, course?.holiday_dates);
  const tier = playerTierGolf(totalPlayers);
  const roundTier = roundCountTierKey(totalRounds, "r3plus");
  const rate = course?.pricing_matrix?.[dayType]?.[tier]?.[roundTier];
  return Number(rate);
}


export function calculateFinalCourseRoundPrice({
  course,
  date,
  period,
  totalPlayers,
  totalRounds,
}) {
  const dayType = resolveDayType(date, period, course?.holiday_dates);
  const tier = playerTierGolf(totalPlayers);
  const roundTier = roundCountTierKey(totalRounds, "r3plus");
  const rate = course?.pricing_matrix?.[dayType]?.[tier]?.[roundTier];
  return Number(rate) * Number(totalPlayers) || 0;
}

/**
 * Sums per-round-per-player prices across the whole booking.
 * @param {{course:object, date:any, period?:'AM'|'PM'}[]} params.rounds -
 *   one entry per round, each with its OWN course doc
 * @param {number} params.totalGuests
 */
export function calculateGolfTotal({ rounds, totalGuests }) {
  const totalRounds = (rounds || []).length;
  const guests = Number(totalGuests) || 0;
  return (rounds || []).reduce((sum, r) => {
    const perPlayer = calculateFinalCourseRoundPrice({
      course: r.course,
      date: r.date,
      period: r.period,
      totalPlayers: guests,
      totalRounds,
    });
    return sum + perPlayer;
  }, 0);
}

// ---------------------------------------------------------------------------
// Transport (vehicle) pricing — matrix lookup, FLAT total (no seat/vehicle-
// count math: "seats don't matter" per admin confirmation)
// ---------------------------------------------------------------------------

function playerTierTransport(players) {
  const n = Number(players) || 0;
  if (n <= 1) return "p1";
  if (n === 2) return "p2";
  return "p3plus";
}

/**
 * Flat transport total for the whole booking — overridden to 0 entirely
 * when vehicle_free_for_one_round applies.
 *
 * @param {object} params
 * @param {object} params.course - governs vehicle_free_for_one_round only
 * @param {object} params.vehicle
 * @param {number} params.totalGuests
 * @param {number} params.numRounds - TOTAL rounds in the booking
 * @param {Date|string} params.date - anchor date for the day-type lookup
 *   (the first round's date — the vehicle is booked once for the whole trip)
 * @param {'AM'|'PM'} [params.period] - anchor period, matching `date`
 */
export function calculateTransportTotal({
  course,
  vehicle,
  totalGuests,
  numRounds,
  date,
  period,
}) {
  if (!vehicle) return { total: 0, isFree: false, trips: 0 };
  if (isVehicleFreeForBooking(course, numRounds)) {
    return { total: 0, isFree: true, trips: 0 };
  }

  const guests = Math.max(0, Number(totalGuests) || 0);
  const trips = Math.max(0, Number(numRounds) || 0) * 2; // 2 trips per round

  const dayType = resolveDayType(date, period, vehicle?.holiday_dates);
  const tier = playerTierTransport(guests); // p1 | p2 | p3plus
  const perPlayerPerTrip =
    Number(vehicle?.pricing_matrix?.[dayType]?.[tier]?.perTrip) || 0;

  return {
    total: perPlayerPerTrip * guests * trips,
    isFree: false,
    perPlayerPerTrip,
    trips,
    dayType,
    tier,
  };
}

// ---------------------------------------------------------------------------
// Accommodation pricing — matrix lookup, golfer chooses share type at
// booking time (requirements #4/#5/#6/#8 now folded into the matrix)
// ---------------------------------------------------------------------------

export const SHARING_TYPES = {
  SINGLE: "single_share",
  TWIN: "twin_share",
};

function roomTypeTier(sharingType) {
  return sharingType === SHARING_TYPES.TWIN ? "twin_share" : "single_share";
}

/**
 * Per-night price.
 * @param {object} params
 * @param {object} params.accommodation
 * @param {Date|string} params.date
 * @param {'AM'|'PM'} [params.period] - see the AM/PM assumption documented
 *   at the top of this file
 * @param {number} params.totalNights - TOTAL nights in the whole stay
 * @param {string} params.sharingType - 'single_share' | 'twin_share', chosen
 *   by the golfer at booking time
 */


export function calculateAccommodationNightPrice({
  accommodation,
  date,
  period,
  totalNights,
  sharingType,
}) {
  const dayType = resolveAccommodationDayType(
    date,
    period,
    accommodation?.holiday_dates,
  );
  const tier = roomTypeTier(sharingType);
  const nTier = nightCountTierKey(totalNights);
  const cell = accommodation?.pricing_matrix?.[dayType]?.[tier];
  const rate = cell?.[nTier] ?? (nTier === "n2plus" ? cell?.n3plus : undefined);
  return Number(rate) || 0;
}

/**
 * @param {object} params
 * @param {object} params.accommodation
 * @param {Date[]|string[]} params.nightDates - one entry per night, in order
 * @param {string} params.sharingType - 'single_share' | 'twin_share'
 * @param {number} [params.numRooms] - rooms booked; each night is charged
 *   once per room. Falls back to totalGuests for bookings made before the
 *   room selector existed (one room per player).
 * @param {Record<string,'AM'|'PM'>} [params.roundPeriodByDate] - map of
 *   'yyyy-MM-dd' -> that day's round period, built by the caller from
 *   roundDates/roundTimes. A night with no matching round defaults to AM.
 * @returns {{ nights: {date:any, price:number}[], total: number }}
 */
export function calculateAccommodationTotal({
  accommodation,
  nightDates,
  sharingType,
  numRooms,
  totalGuests,
  roundPeriodByDate = {},
}) {
  const totalNights = (nightDates || []).length;
  const rooms = Math.max(1, Number(numRooms ?? totalGuests) || 1);

  if (totalNights === 0) return { nights: [], total: 0, perPersonPrice: 0 };

  // single rate, looked up from the first night
  const firstDate = nightDates[0];
  const firstPeriod = roundPeriodByDate?.[toDateKey(firstDate)] || "AM";
  const perPersonPrice = calculateAccommodationNightPrice({
    accommodation,
    date: firstDate,
    period: firstPeriod,
    totalNights,
    sharingType,
  });

  // total = price * total guests * total nights * total rooms
  const total = perPersonPrice * totalNights * rooms;

  // console.log({ perPersonPrice, totalNights, rooms, total,totalGuests });
  // keep a per-night breakdown for the UI (same rate each night)
  const nights = nightDates.map((date) => ({
    date,
    perPersonPrice,
    price: perPersonPrice * rooms,
  }));

  return { nights, total, perPersonPrice };
}

// ---------------------------------------------------------------------------
// Rooms — default one room per player; up to 3 players may share a room.
// ---------------------------------------------------------------------------

export const MAX_PLAYERS_PER_ROOM = 3;

export function minRoomsForPlayers(players) {
  return Math.max(1, Math.ceil((Number(players) || 1) / MAX_PLAYERS_PER_ROOM));
}

export function defaultRoomsForPlayers(players) {
  return Math.max(1, Number(players) || 1);
}

// ---------------------------------------------------------------------------
// Currency display — every price on the site reads "SGD 1,234".
// ---------------------------------------------------------------------------

export function formatSGD(amount) {
  const n = Number(amount) || 0;
  return `SGD ${n.toLocaleString("en-SG", { maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// Ferry pricing — unchanged: one-way fare, always charged × 2 (inbound +
// outbound), regardless of the ferry's `roundTrip` flag.
// ---------------------------------------------------------------------------

export function calculateFerryTotal({ ferry, totalGuests }) {
  const guests = Math.max(0, Number(totalGuests) || 0);
  const oneWayPrice = Number(ferry?.price) || 0;
  return oneWayPrice * guests;
}

// ---------------------------------------------------------------------------
// Top-level booking total — the one function everything downstream
// (RecommendedTravelStep, checkout, order summary) should call.
// ---------------------------------------------------------------------------

/**
 * @param {object} params
 * @param {object} [params.vehicle]
 * @param {object} params.vehicleCourse - course governing vehicle_free_for_one_round (usually rounds[0].course)
 * @param {object} [params.ferry]
 * @param {object} [params.accommodation]
 * @param {number} params.totalGuests
 * @param {{course:object, date:any, period?:'AM'|'PM'}[]} params.rounds
 * @param {Date[]|string[]} [params.nightDates]
 * @param {string} [params.sharingType]
 * @param {number} [params.numRooms]
 * @param {Record<string,'AM'|'PM'>} [params.roundPeriodByDate]
 */
export function calculateBookingTotal({
  vehicle,
  vehicleCourse,
  ferry,
  accommodation,
  totalGuests,
  rounds,
  nightDates = [],
  sharingType,
  numRooms,
  roundPeriodByDate,
}) {
  const golfTotal = calculateGolfTotal({ rounds, totalGuests });

  const firstRound = (rounds || [])[0];
  const vehicleTotal = vehicle
    ? calculateTransportTotal({
        course: vehicleCourse,
        vehicle,
        totalGuests,
        numRounds: rounds?.length || 0,
        date: firstRound?.date,
        period: firstRound?.period,
      }).total
    : 0;

  const ferryTotal = ferry ? calculateFerryTotal({ ferry, totalGuests }) : 0;

  const accommodationResult = accommodation
    ? calculateAccommodationTotal({
        accommodation,
        nightDates,
        sharingType,
        numRooms,
        totalGuests,
        roundPeriodByDate,
      })
    : { nights: [], total: 0 };

  return {
    golfTotal,
    vehicleTotal,
    ferryTotal,
    accommodationTotal: accommodationResult.total,
    accommodationNights: accommodationResult.nights,
    grandTotal:
      golfTotal + vehicleTotal + ferryTotal + accommodationResult.total,
  };
}
